import produce from "immer";
import { keyBy, values } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import useSWR from "swr";
import { ApiResponse, fetchApi } from "../../utils/api";
import {
  CommissionReport,
  CommissionType,
  CommissionValues,
  DataById
} from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import usePrevious from "../../utils/usePrevious";
import { LoadingKey, useLoading } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";
import CommissionPaymentSummarySidebar, {
  CommissionPaymentSummarySidebarConfig
} from "./CommissionPaymentSummarySidebar";
import ContactCommissionsSummarySidebar, {
  ContactCommissionsSummarySidebarConfig
} from "./ContactCommissionsSummarySidebar";
import GenerateCommissionReportSidebar, {
  GenerateCommissionReportSidebarConfig
} from "./GenerateCommissionReportSidebar";
import HntPaymentSidebar, {
  HntPaymentSidebarConfig
} from "./HntPaymentSidebar";

interface Props {}

export type ContextValue = {
  commissionReports: DataById<CommissionReport>;
  currentCommissionReport: CommissionReport;
  hasPendingCommissionReports: CommissionReport | undefined;
  fetchCommissionReports: () => Promise<void>;
  fetchCommissionReportById: (commissionReportId: string) => Promise<void>;
  generateCommissionReport: (
    values: CommissionReportValues,
    contactUuids: string[]
  ) => Promise<ApiResponse<CommissionReport> | undefined>;
  removeCommissionReport: (
    commissionReportId: string
  ) => Promise<ApiResponse<[]> | undefined>;
  fetchCommissionReportDetails: (
    values: CommissionReportDetailsValues
  ) => Promise<ApiResponse<CommissionValues[]>>;
  contactCommissionsSummarySidebarConfig: ContactCommissionsSummarySidebarConfig;
  setContactCommissionsSummarySidebarConfig: React.Dispatch<
    React.SetStateAction<ContactCommissionsSummarySidebarConfig>
  >;
  commissionPaymentSummarySidebarConfig: CommissionPaymentSummarySidebarConfig;
  setCommissionPaymentSummarySidebarConfig: React.Dispatch<
    React.SetStateAction<CommissionPaymentSummarySidebarConfig>
  >;
  hntPaymentSidebarConfig: HntPaymentSidebarConfig;
  setHntPaymentSidebarConfig: React.Dispatch<
    React.SetStateAction<HntPaymentSidebarConfig>
  >;
  generateCommissionReportSidebarConfig: GenerateCommissionReportSidebarConfig;
  setGenerateCommissionReportSidebarConfig: React.Dispatch<
    React.SetStateAction<GenerateCommissionReportSidebarConfig>
  >;
  currentCommissionReportDetails: Partial<CommissionReport> | undefined;
  setCurrentCommissionReportDetails: React.Dispatch<
    React.SetStateAction<Partial<CommissionReport> | undefined>
  >;
};

export const CommissionReportsContext = React.createContext<
  ContextValue | undefined
>(undefined);

export const CommissionReportsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { currentWorkspaceId, setWorkspaces, currentWorkspaceIsLocked } =
    useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const [commissionReports, setComissionsReports] = useState<{
    [id: string]: CommissionReport;
  }>({});
  const [
    contactCommissionsSummarySidebarConfig,
    setContactCommissionsSummarySidebarConfig
  ] = useState<ContactCommissionsSummarySidebarConfig>({ show: false });
  const [
    commissionPaymentSummarySidebarConfig,
    setCommissionPaymentSummarySidebarConfig
  ] = useState<CommissionPaymentSummarySidebarConfig>({ show: false });
  const [hntPaymentSidebarConfig, setHntPaymentSidebarConfig] =
    useState<HntPaymentSidebarConfig>({ show: false });
  const [
    generateCommissionReportSidebarConfig,
    setGenerateCommissionReportSidebarConfig
  ] = useState<GenerateCommissionReportSidebarConfig>({
    show: false,
    contacts: []
  });
  const { pathname } = useLocation();
  const [currentCommissionReportDetails, setCurrentCommissionReportDetails] =
    useState<Partial<CommissionReport>>();
  const detailsPageMatch = matchPath<{
    section: string;
    subsection: string;
    id: string;
  }>(pathname, {
    path: ["/:section/:subsection/:id"]
  });

  const fetchCommissionReports = useCallback(async () => {
    if (!currentWorkspaceId) return;

    addLoader(LoadingKey.workspaceCommissionReports);

    try {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/reports-list`
      );
      const apiResponse: ApiResponse<CommissionReport[]> =
        await response.json();

      if (apiResponse.status) {
        setComissionsReports(keyBy(apiResponse.data, "uuid"));
      }
    } catch {}

    removeLoader(LoadingKey.workspaceCommissionReports);
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const fetchCommissionReportById = useCallback(
    async (commissionReportId: string) => {
      if (!currentWorkspaceId || !commissionReportId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/report/${commissionReportId}`
        );

        const apiResponse: ApiResponse<CommissionReport> =
          await response.json();

        if (apiResponse.status) {
          setCurrentCommissionReportDetails(apiResponse.data);
        }
      } catch {}
    },
    [currentWorkspaceId]
  );

  const hasPendingCommissionReports = useMemo(
    () =>
      values(commissionReports).find(
        (item: CommissionReport) => item.status === "pending"
      ),
    [commissionReports]
  );

  const generateCommissionReport = useCallback(
    async (values: CommissionReportValues, contactUuids: string[]) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/reports`,
        {
          method: "POST",
          body: JSON.stringify({ ...values, contacts: contactUuids })
        }
      );

      const apiResponse: ApiResponse<CommissionReport> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.commissionReport += 1;
          })
        );

        await fetchCommissionReports();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchCommissionReports, setWorkspaces]
  );

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setComissionsReports({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  const removeCommissionReport = useCallback(
    async (commissionReportId: string) => {
      if (!currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/reports/${commissionReportId}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (apiResponse.status) {
          setWorkspaces((prevState) =>
            produce(prevState, (draft) => {
              if (!draft[currentWorkspaceId].counts) return;

              draft[currentWorkspaceId].counts.commissionReport -= 1;
            })
          );

          await fetchCommissionReports();
        }

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchCommissionReports, setWorkspaces]
  );

  const fetchCommissionReportDetails = useCallback(
    async (values: CommissionReportDetailsValues) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/reports/details`,
          {
            method: "POST",
            body: JSON.stringify(values)
          }
        );

        const apiResponse: ApiResponse<CommissionValues[]> =
          await response.json();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId]
  );

  useEffect(() => {
    if (currentWorkspaceIsLocked) return;

    fetchCommissionReports();
  }, [currentWorkspaceIsLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  useSWR(
    currentWorkspaceId ? `${currentWorkspaceId}/reports` : null,
    fetchCommissionReports,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false,
      refreshInterval: hasPendingCommissionReports ? 30000 : undefined
    }
  );

  const currentCommissionReport: CommissionReport = useMemo(() => {
    if (!detailsPageMatch?.params.id)
      return {
        uuid: "",
        created_at: "",
        created_by: "",
        deleted_at: "",
        start_date: "",
        end_date: "",
        timezone: "",
        currency: CurrencyKey.hnt,
        total_commission_amount: 0,
        report: [],
        identifier: 0,
        current_block: 0,
        status: "pending",
        title: "",
        contact_payments: [],
        contacts: [],
        legacy_report: "",
        time: 0
      };

    let report = commissionReports[detailsPageMatch.params.id] || {};

    if (currentCommissionReportDetails) {
      report = {
        ...report,
        ...currentCommissionReportDetails
      };
    }

    return report;
  }, [
    commissionReports,
    currentCommissionReportDetails,
    detailsPageMatch?.params.id
  ]);

  useEffect(() => {
    if (
      detailsPageMatch?.params.subsection !== "commission-reports" ||
      !detailsPageMatch?.params.id
    ) {
      return;
    }

    fetchCommissionReportById(detailsPageMatch?.params.id);

    return () => {
      setCurrentCommissionReportDetails(undefined);
    };
  }, [
    fetchCommissionReportById,
    detailsPageMatch?.params.id,
    setCurrentCommissionReportDetails,
    detailsPageMatch?.params.subsection
  ]);

  const value = useMemo(
    () => ({
      commissionReports,
      fetchCommissionReports,
      generateCommissionReport,
      removeCommissionReport,
      contactCommissionsSummarySidebarConfig,
      setContactCommissionsSummarySidebarConfig,
      commissionPaymentSummarySidebarConfig,
      setCommissionPaymentSummarySidebarConfig,
      hntPaymentSidebarConfig,
      setHntPaymentSidebarConfig,
      generateCommissionReportSidebarConfig,
      setGenerateCommissionReportSidebarConfig,
      fetchCommissionReportDetails,
      hasPendingCommissionReports,
      fetchCommissionReportById,
      currentCommissionReportDetails,
      setCurrentCommissionReportDetails,
      currentCommissionReport
    }),
    [
      commissionReports,
      fetchCommissionReports,
      generateCommissionReport,
      removeCommissionReport,
      contactCommissionsSummarySidebarConfig,
      commissionPaymentSummarySidebarConfig,
      hntPaymentSidebarConfig,
      generateCommissionReportSidebarConfig,
      fetchCommissionReportDetails,
      hasPendingCommissionReports,
      fetchCommissionReportById,
      currentCommissionReportDetails,
      setCurrentCommissionReportDetails,
      currentCommissionReport
    ]
  );

  return (
    <CommissionReportsContext.Provider value={value} {...otherProps}>
      <ContactCommissionsSummarySidebar />
      <CommissionPaymentSummarySidebar />
      <HntPaymentSidebar />
      <GenerateCommissionReportSidebar />
      {children}
    </CommissionReportsContext.Provider>
  );
};

export const useCommissionReports = (): ContextValue => {
  const context = useContext(CommissionReportsContext);

  if (context === undefined) {
    throw new Error(
      "useCommissionReports must be used within an CommissionReportsProvider"
    );
  }

  return context;
};

//
// Utils
//

interface CommissionReportValues {
  start_date: string;
  end_date: string;
  currency: CurrencyKey | null;
  timezone: string;
}

interface CommissionReportDetailsValues {
  hotspotId: string;
  installed_at: string | null;
  uninstalled_at: string | null;
  start_date?: string | null;
  end_date?: string | null;
  currency: CurrencyKey;
  timezone: string;
  type: CommissionType;
  cron_expression: string | null;
  value: number;
  commission_start_date: string;
  commission_end_date?: string | null;
}

export const getCommissionReportTitle = ({
  title,
  identifier
}: CommissionReport) => `${title || "Commission report"} #${identifier}`;
